import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { ENDPOINTS, expenseFields, incomeFields, pnlOptions, staticFields } from '../helpers/constants';
import {
  calculateCommissionDue,
  calculateRebate,
  calculateTotalGP,
  getCommissionInventoryUnit,
  getPnlValue
} from '../helpers/global';
import SlidingSideBar from './SlidingSideBar/SlidingSideBar';
import CommissionValuesSidebar from './inventory/CommissionValuesSidebar';
import EditInventory from './inventory/EditInventory';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../helpers/api';

const STATIC_FIELD_LABELS_TO_CUSTOMIZE = new Set(['invoice', 'commission due']);

const orderedLabels = [
  'Buyer',
  'Salesperson',
  'Status',
  'Funding Status',
  'Size Overall',
  'Invoice',
  'Del & set',
  'Air Condition',
  'Pad',
  'Concessions',
  'Commission Due',
  'Team Leader',
  'Admin',
  'Service',
  'Bank fees',
  'SP/Comm',
  'Rebate',
  'Frg. Reim',
  'NPS',
  'Doc Prep',
  'TOTAL GP',
  'Bank',
  'Factory',
  'Date of Deposit'
].map(label => label.toLowerCase());

const formatAmount = value => {
  if (value === null || value === undefined || value === '') return '-';
  return Number.isFinite(Number(value)) ? Number(value).toFixed(2) : value;
};

const buildPnlInputValues = (contract, appChoices) =>
  pnlOptions.map(option => ({
    ...option,
    value: option.key === 'pnlRebate' ? calculateRebate(contract) : getPnlValue(contract, option.key, appChoices)
  }));

const PnLSidebar = ({ contract, onClose, onUpdate, appChoices, visible }) => {
  const inventoryUnit = getCommissionInventoryUnit(contract);

  const [inputValues, setInputValues] = useState(() => buildPnlInputValues(contract, appChoices));
  const [commissionValues, setCommissionValues] = useState(() => ({
    commissionSheetOptions: inventoryUnit?.commissionSheetOptions || [],
    commissionSheetLessOptions: inventoryUnit?.commissionSheetLessOptions || []
  }));
  const [showCommissionSidebar, setShowCommissionSidebar] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);
  const [updatingPnl, setUpdatingPnl] = useState(false);

  useEffect(() => {
    if (!visible) {
      setShowCommissionSidebar(false);
      setEditingInventory(null);
      return;
    }

    const unit = getCommissionInventoryUnit(contract);
    setInputValues(buildPnlInputValues(contract, appChoices));
    setCommissionValues({
      commissionSheetOptions: unit?.commissionSheetOptions || [],
      commissionSheetLessOptions: unit?.commissionSheetLessOptions || []
    });
    setShowCommissionSidebar(false);
  }, [contract, visible, appChoices]);

  const contractStatus = useMemo(() => (contract?.newStatus === 'Contracted' ? 'Contracted' : 'Working'), [contract]);

  const previewRecord = useMemo(() => {
    const unit = getCommissionInventoryUnit(contract);
    if (!unit) return contract;

    const pnlOptionsPayload = inputValues
      ?.filter(option => option.key !== 'pnlRebate')
      .map(({ key, value }) => ({ key, value: Number(value ?? 0) }));
    const rebateValue = inputValues?.find(option => option?.key === 'pnlRebate')?.value;
    const updatedInventory = {
      ...unit,
      commissionSheetOptions: commissionValues.commissionSheetOptions,
      commissionSheetLessOptions: commissionValues.commissionSheetLessOptions,
      pnlOptions: pnlOptionsPayload,
      pnlRebate:
        rebateValue === null || rebateValue === undefined || rebateValue === '' ? null : Number(rebateValue)
    };

    if (contract?.newStatus === 'Working') {
      return { ...contract, inventoryUnit: [updatedInventory] };
    }

    return { ...contract, inventoryUnit: updatedInventory };
  }, [contract, inputValues, commissionValues]);

  const totalGP = calculateTotalGP(previewRecord, appChoices, expenseFields, incomeFields(contractStatus));
  const commissionDue = calculateCommissionDue({ ...previewRecord, newStatus: contractStatus });
  const invoiceValue = inventoryUnit?.invoice;

  const handleInputChange = (key, value) => {
    setInputValues(prev =>
      prev.map(option => (option.key === key ? { ...option, value: value === '' ? null : Number(value) } : option))
    );
  };

  const handleUpdate = async () => {
    const unit = getCommissionInventoryUnit(contract);
    if (!unit?._id) {
      return toast.error('No inventory unit linked to this deal.');
    }

    setUpdatingPnl(true);
    toast.info('Updating P&L details...');

    const pnlOptionsPayload = inputValues
      ?.filter(option => option.key !== 'pnlRebate')
      .map(({ key, value }) => ({ key, value: Number(value ?? 0) }));

    const rebateValue = inputValues?.find(option => option?.key === 'pnlRebate')?.value;

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(unit._id),
      method: 'PUT',
      requestBody: {
        commissionSheetOptions: commissionValues.commissionSheetOptions,
        commissionSheetLessOptions: commissionValues.commissionSheetLessOptions,
        pnlOptions: pnlOptionsPayload,
        pnlRebate: rebateValue === null || rebateValue === undefined || rebateValue === '' ? null : Number(rebateValue)
      }
    });

    setUpdatingPnl(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('P&L details updated successfully!');
    onUpdate(response);
  };

  const allFields = useMemo(() => {
    const customizedStaticFields = staticFields(contractStatus).filter(
      field => !STATIC_FIELD_LABELS_TO_CUSTOMIZE.has(field.label.toLowerCase())
    );

    return [
      { label: 'Invoice', type: 'invoice', value: invoiceValue },
      {
        label: 'Commission Due',
        type: 'commission',
        value: commissionDue
      },
      ...inputValues.map(f => ({ ...f, type: 'input' })),
      ...customizedStaticFields.map(f => ({ ...f, type: 'static' })),
      {
        label: 'Total GP',
        value: () => totalGP?.toFixed(2) || '0',
        type: 'static'
      }
    ].sort((a, b) => orderedLabels.indexOf(a.label.toLowerCase()) - orderedLabels.indexOf(b.label.toLowerCase()));
  }, [inputValues, contractStatus, totalGP, invoiceValue, commissionDue]);

  const unitLocationId = inventoryUnit?.unitLocation?._id || inventoryUnit?.unitLocation;
  const numberOfSections = inventoryUnit?.numberOfSections;

  return (
    <>
      <SlidingSideBar visible={visible} onClose={onClose} title="P&L Details">
        <Container fluid>
          <Table bordered responsive>
            <thead>
              <tr className="bg-primary text-white">
                <th className="py-2">Category</th>
                <th className="py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {allFields.map(field => (
                <tr
                  key={field.label}
                  className={
                    field.type === 'static' || field.type === 'commission' || field.type === 'invoice' ? 'bg-light' : ''
                  }
                >
                  <td>{field.label}</td>
                  <td>
                    {field.type === 'input' ? (
                      <Form.Control
                        type="number"
                        placeholder="Enter amount"
                        size="sm"
                        value={field.value !== null && field.value !== undefined ? field.value : ''}
                        onChange={e => handleInputChange(field.key, e.target.value)}
                      />
                    ) : field.type === 'invoice' ? (
                      <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <span className="font-weight-bold mr-2">{formatAmount(field.value)}</span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="tinyFont mt-1 mt-sm-0"
                          disabled={!inventoryUnit}
                          onClick={() => setEditingInventory(inventoryUnit)}
                        >
                          Edit Unit
                        </Button>
                      </div>
                    ) : field.type === 'commission' ? (
                      <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <span className="font-weight-bold mr-2">{formatAmount(field.value)}</span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="tinyFont mt-1 mt-sm-0"
                          onClick={() => setShowCommissionSidebar(true)}
                        >
                          Edit Commission Values
                        </Button>
                      </div>
                    ) : (
                      <span className="font-weight-bold">{field.value(contract)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Container>
        <div className="bar-sticky-footer">
          <Row>
            <Col className="d-flex justify-content-end">
              <Button size="sm" variant="secondary" className="mr-2" onClick={onClose} disabled={updatingPnl}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={handleUpdate} disabled={updatingPnl}>
                {updatingPnl ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
                {updatingPnl ? 'Updating...' : 'Update'}
              </Button>
            </Col>
          </Row>
        </div>
      </SlidingSideBar>

      <CommissionValuesSidebar
        visible={showCommissionSidebar}
        onClose={() => setShowCommissionSidebar(false)}
        value={commissionValues}
        onChange={setCommissionValues}
        appChoices={appChoices}
        unitLocationId={unitLocationId}
        numberOfSections={numberOfSections}
      />

      <SlidingSideBar
        fullScreen
        visible={!!editingInventory}
        onClose={() => setEditingInventory(null)}
        title="Inventory Details"
      >
        {editingInventory && (
          <EditInventory
            id={editingInventory['_id']}
            inventoryFromSearch={editingInventory}
            appChoices={appChoices}
            fromSearch
            deletable={false}
            onInventoryEdit={updatedInventory => {
              setEditingInventory(null);
              onUpdate(updatedInventory);
            }}
          />
        )}
      </SlidingSideBar>
    </>
  );
};

export default PnLSidebar;
